'use client';

import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

/**
 * Map 컴포넌트 Props 인터페이스
 * 
 * 지도 컴포넌트의 기본 설정을 정의합니다.
 */
interface MapProps {
  center: [number, number];
  zoom?: number;
  className?: string;
}

/**
 * Leaflet 지도 인스턴스 인터페이스
 * 
 * Leaflet 지도의 주요 메서드들을 정의합니다.
 */
interface LeafletMap {
  remove: () => void;
  invalidateSize: () => void;
  whenReady: (callback: () => void) => void;
}

/**
 * 지도 컴포넌트
 * 
 * Leaflet을 사용하여 인터랙티브한 지도를 표시하는 컴포넌트입니다.
 * SSR을 방지하기 위해 동적으로 로드되며, 커스텀 마커와 팝업을 지원합니다.
 * 
 * 주요 기능:
 * - OpenStreetMap 타일 레이어 사용
 * - 커스텀 마커 아이콘 (충북대학교 RISE사업단 위치)
 * - 정보 팝업 (자동 열기)
 * - 줌 컨트롤 (우상단)
 * - 반응형 디자인
 * - SSR 호환성
 */
const MapComponent = ({ center, zoom = 15, className = '' }: MapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<LeafletMap | null>(null);
  const [isClient, setIsClient] = useState(false);

  // 클라이언트 사이드 렌더링 확인
  useEffect(() => {
    setIsClient(true);
  }, []);

  // 지도 초기화 및 설정
  useEffect(() => {
    if (!isClient || !mapRef.current || mapInstanceRef.current) return;

    const initMap = async () => {
      try {
        const L = await import('leaflet');

        // Leaflet CSS 로드 (중복 로드 방지)
        if (!document.querySelector('link[href*="leaflet"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }

        if (!mapRef.current) return;

        // 지도 인스턴스 생성
        const map = L.map(mapRef.current, {
          zoomControl: false,
          center: center,
          zoom: zoom
        });
        mapInstanceRef.current = map as LeafletMap;

        // OpenStreetMap 타일 레이어 추가
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '',
          maxZoom: 19,
        }).addTo(map);

        // 커스텀 줌 컨트롤 추가
        L.control.zoom({
          position: 'topright',
          zoomInTitle: '확대',
          zoomOutTitle: '축소'
        }).addTo(map);

        /**
         * 커스텀 마커 아이콘 생성 함수
         * 
         * 충북대학교 RISE사업단을 나타내는 고유한 마커 디자인을 생성합니다.
         * 
         * @returns 커스텀 마커 아이콘
         */
        const createCustomIcon = () => {
          return L.divIcon({
            className: 'custom-marker',
            html: `
              <div style="
                position: relative;
                width: 48px;
                height: 48px;
              ">
                <!-- 마커 핀 -->
                <div style="
                  position: absolute;
                  top: 0;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 30px;
                  height: 30px;
                  background: #b62b5c;
                  border-radius: 50% 50% 50% 0;
                  transform: translateX(-50%) rotate(-45deg);
                  border: 4px solid white;
                  box-shadow: 0 3px 12px rgba(0,0,0,0.3);
                "></div>
                
                <!-- 마커 중앙 원 -->
                <div style="
                  position: absolute;
                  top: 9px;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 12px;
                  height: 12px;
                  background: #ffffff;
                  border-radius: 50%;
                  z-index: 1;
                "></div>
                
                <!-- 마커 그림자 -->
                <div style="
                  position: absolute;
                  bottom: 0px;
                  left: 50%;
                  transform: translateX(-50%);
                  width: 8px;
                  height: 8px;
                  background: rgba(0,0,0,0.2);
                  border-radius: 50%;
                "></div>
              </div>
            `,
            iconSize: [48, 48],
            iconAnchor: [24, 48],
          });
        };

        // 커스텀 마커 추가
        const marker = L.marker(center, { icon: createCustomIcon() }).addTo(map);

        // 마커 팝업 설정
        marker.bindPopup(`
          <div style="text-align: center; padding: 8px;">
            <p class="text-lg font-bold !m-0">충북대학교 RISE사업단</p>
            <p class="text-base !m-0">E9동 906호</p>
          </div>
        `, {
          autoPanPadding: [50, 50],
          closeButton: false,
          className: 'custom-popup'
        });

        // 지도 준비 완료 시 처리
        map.whenReady(() => {
          map.invalidateSize();

          // 마커 팝업 자동 열기 (200ms 지연)
          setTimeout(() => {
            marker.openPopup();
          }, 200);
        });

      } catch (error) {
        // 지도 초기화 오류 처리
      }
    };

    initMap();

    // 컴포넌트 언마운트 시 지도 정리
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [isClient, center, zoom]);

  // 클라이언트 사이드가 아닌 경우 로딩 상태 표시
  if (!isClient) {
    return (
      <div
        className={`w-full h-180 rounded-lg overflow-hidden bg-background flex items-center justify-center ${className}`}
        style={{ minHeight: '720px' }}
      >
        <div className="text-foreground">지도를 불러오는 중...</div>
      </div>
    );
  }

  // 지도 컨테이너 반환
  return (
    <div
      ref={mapRef}
      className={`w-full h-180 rounded-lg overflow-hidden ${className}`}
      style={{ minHeight: '720px' }}
    />
  );
};

/**
 * 동적 지도 컴포넌트
 * 
 * SSR을 방지하기 위해 동적으로 로드되는 지도 컴포넌트입니다.
 * 로딩 중에는 로딩 메시지를 표시합니다.
 */
export default dynamic(() => Promise.resolve(MapComponent), {
  ssr: false,
  loading: () => (
    <div className="w-full h-150 rounded-lg overflow-hidden bg-background flex items-center justify-center">
      <div className="text-muted">지도를 불러오는 중...</div>
    </div>
  ),
}); 